import Header from "./components/Header/Header"
import SideMenu from "./components/Sidebar/Sidebar"
import { useState, useEffect, Suspense, lazy } from "react"
import { Provider } from "react-redux"
import {
  coreStore,
  runTimeoutForRefreshToken,
  SET_ID_TOKEN,
  SET_USER_TOKEN
} from "@forcepoint/platform-coreui"
import GlobalLoader from "./components/GlobalLoader/GlobalLoader"
import {
  NeoErrorBoundary,
  NeoThemeProvider,
  Intl,
  NeoComponents,
  Toastr,
  messages,
  FoneAlert
} from "@forcepoint/platform-utilityui"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import store from "./common/state/app.state"

const EulaAcceptanceModal = lazy(() => import("./components/Eula/EulaAcceptanceModal"))
const UserProfileContainer = lazy(() => import("./components/UserProfile/UserProfileContainer"))
const ContactSupport = lazy(() => import("./components/ContactSupport/ContactSupport"))

const requestCrossSiteStorageAccess = async (iframe: HTMLIFrameElement) => {
  try {
    if (!document.requestStorageAccess) {
      console.warn(
        "[navigationui] Storage Access API not supported in this browser"
      )
      showStorageBlockedMessage()
      return
    }

    console.log(
      "[navigationui] Requesting cross-site storage access from user..."
    )
    await document.requestStorageAccess()
    console.log("[navigationui] Storage access granted — retrying auth")
    iframe.remove()
    sessionStorage.removeItem("cookieCheck")
    sessionStorage.removeItem("authIframeTimeout")
    window.location.reload()
  } catch (err) {
    console.warn("[navigationui] Storage access denied or error:", err)
    showStorageBlockedMessage()
  }
}

const showStorageBlockedMessage = () => {
  coreStore.dispatch({
    type: "setFoneAlertConfig",
    status: true,
    alertProps: {
      type: "error",
      message:
        "Third-party cookies are blocked. To log in, " +
        "enable cross-site cookies in your browser settings, " +
        "or try using a different browser privacy mode."
    }
  })
}

export const SuspenseWithLoader = ({ children, Loader = GlobalLoader }) => (
  <Suspense fallback={<Loader />}>
    {children}
  </Suspense>
)

let navigationSubscription = null
/**
 * Navigation UI root.
 * @param props
 * @returns
 */
export default function Root() {
  // -----------------------------------------------------------------------------------------
  // ---------------------------------- Component States ------------------------------------
  // ----------------------------------------------------------------------------------------

  /**
   * To store state data
   */
  const stateData = coreStore.getState()

  /**
   * To show sidebar
   */
  const [showSidebar, setShowSidebar] = useState<boolean>(false)

  /**
   * To set app title
   */
  const [appTitle, setAppTitle] = useState<string>(stateData.appTitle)

  /**
   * To set applications
   */
  const [applications, setApplications] = useState(stateData.applications)

  /**
   * To set active app
   */
  const [activeApp, setActiveApp] = useState(stateData.activeApp)

  /**
   * To set global loader
   */
  const [globalLoader, setGlobalLoader] = useState<boolean>(
    stateData.showGlobalLoader
  )

  /**
   * To set page title
   */
  const [pageTitle, setPageTitle] = useState<string>(stateData.pageTitle)

  /**
   * To show sidebar
   */
  const [showEulaAcceptance, setShowEulaAcceptance] = useState(false)

  /**
   * To store eula details
   */
  const [eulaObj, setEulaObj] = useState({})

  /**
   * To show white loader
   */
  const [showWhiteLoader, setShowWhiteLoader] = useState<boolean>(
    stateData.showWhiteLoader
  )

  /**
   * To store fone alert config
   */
  const [foneAlertConfig, setFoneAlertConfig] = useState({
    status: false,
    alertProps: null
  })

  const sideBarData = sessionStorage.getItem("isExpanded")

  const [isExpandedSideBar, setIsExpandedSideBar] = useState(
    JSON.parse(sideBarData)
  )

  useEffect(() => {
    if (isExpandedSideBar) {
      document.querySelector("body").classList.add("expanded-sidebar")
    } else {
      document.querySelector("body").classList.remove("expanded-sidebar")
    }
  }, [isExpandedSideBar])

  /**
   * To store event method
   */
  const eventMethod = window.addEventListener
    ? "addEventListener"
    : "attachEvent"

  /**
   * To store window event
   */
  const windowEvent = window[eventMethod]

  /**
   * To store message event
   */
  const messageEvent = eventMethod == "attachEvent" ? "onmessage" : "message"

  // ----------------------------------------------------------------------------------------
  // --------------------------------- Component Effects ------------------------------------
  // ----------------------------------------------------------------------------------------

  const updateGlobalLoader = (storeData: any) => {
    setGlobalLoader(storeData.showGlobalLoader) // Update global loader show status
  }

  const updateEulaConfig = (storeData: any) => {
    let eulaConfig = null
    if (storeData.eulaConfig?.pendingEulas?.length > 0) {
      eulaConfig = storeData.eulaConfig
      setShowEulaAcceptance(true)
      setGlobalLoader(false)
    }
    setEulaObj(eulaConfig)
  }

  const updateSidebar = (storeData: any) => {
    const hasSidebar = storeData.activeApp?.modules?.uiConfig?.enableSidebar
    if (
      hasSidebar ||
      window.location.pathname === "/editprofile" ||
      window.location.pathname.endsWith("/contact-support") ||
      window.location.pathname.startsWith("/home")
    ) {
      document.body.classList.add("with-sidebar")
      setShowSidebar(true)
    } else {
      document.body.classList.remove("with-sidebar")
      setShowSidebar(false)
    }
  }

  const updateAppDetails = (storeData: any) => {
    setActiveApp(storeData.activeApp)
    setAppTitle(storeData.appTitle)
    setPageTitle(storeData.pageTitle)
  }

  const updateMountedApps = (storeData: any) => {
    storeData.mountedApps.forEach((mountedApp: any) => {
      if (mountedApp.location) {
        const element: HTMLElement = document.getElementById(
          `single-spa-application:${mountedApp.SPAName}`
        )
        element?.classList.add("SPA-Pos-" + mountedApp?.location)
        if (mountedApp.location === "header") {
          const shellMenu: HTMLElement = document.querySelector(".shell-menu")
          if (element) {
            element.style.right = `${shellMenu?.offsetWidth + 44}px`
          }
        }
      }
    })
  }

  if (!navigationSubscription) {
    navigationSubscription = coreStore.subscribe(() => {
      const storeData = coreStore.getState() // Get state data

      setShowWhiteLoader(storeData.showWhiteLoader) // Update white loader
      updateGlobalLoader(storeData) // Update global loader
      updateEulaConfig(storeData) // Update EULA config
      setFoneAlertConfig(storeData.foneAlertConfig) // Set fone alert config
      if (
        storeData.activeApp ||
        window.location.pathname === "/editprofile" ||
        window.location.pathname.endsWith("/contact-support") ||
        window.location.pathname.startsWith("/home")
      ) {
        updateAppDetails(storeData) // Update app details
        updateSidebar(storeData) // Update sidebar visibility and menus
      } else {
        setActiveApp(null)
        setPageTitle(storeData.pageTitle)
        setAppTitle(null)
        setShowSidebar(false)
        document.body.classList.remove("with-sidebar")
      }

      if (storeData.applications.length > 0) {
        setApplications(storeData.applications) // Set applications
        const shellLoader:HTMLElement = document.querySelector(".skeleton-loader")
        if (shellLoader) {
          shellLoader.style.opacity = "0"
          shellLoader.style.pointerEvents = 'none';
          shellLoader.addEventListener('transitionend', () => {
            shellLoader.remove();
          }, { once: true });
        }
      }

      updateMountedApps(storeData) // Update mounted apps if any
    })
  }

  /**
   * Core store subscriber call..
   */
  useEffect(() => {
    // Named handlers + cleanup (fixes the listener-leak: the previous
    // anonymous listener was never removed, so each remount stacked another
    // copy). Handles token refresh, FPN-6458 storage-access-blocked, and the
    // authIframeTimeout event.
    const handleNewToken = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return false
      if (event.data.message === "NEW_TOKEN_RECEIVED") {
        document.querySelector(".authIframe")?.remove()
        sessionStorage.removeItem("cookieCheck")
        sessionStorage.removeItem("authIframeTimeout")
        coreStore.dispatch({
          type: SET_USER_TOKEN,
          userToken: event.data.decodedUserToken
        })
        coreStore.dispatch({
          type: SET_ID_TOKEN,
          idToken: event.data.decodedIdToken,
          idTokenEncoded: event.data.idTokenEncoded
        })
        runTimeoutForRefreshToken(event.data.dateInterval)
      } else if (event.data.message === "STORAGE_ACCESS_BLOCKED") {
        document.querySelector(".authIframe")?.remove()
        sessionStorage.removeItem("cookieCheck")
        sessionStorage.removeItem("authIframeTimeout")
        showStorageBlockedMessage()
      }
    }

    const handleIframeTimeout = () => {
      const iframe = document.querySelector(".authIframe") as HTMLIFrameElement
      if (iframe) {
        requestCrossSiteStorageAccess(iframe)
      }
    }

    windowEvent(messageEvent, handleNewToken, false)
    window.addEventListener("authIframeTimeout", handleIframeTimeout)

    return () => {
      window.removeEventListener(messageEvent as any, handleNewToken)
      window.removeEventListener("authIframeTimeout", handleIframeTimeout)
    }
  }, [])

  /**
   * On Destory hook
   */
  useEffect(
    () => () => {
      if (navigationSubscription) {
        navigationSubscription()
        navigationSubscription = null
      }
    },
    []
  )

  // -----------------------------------------------------------------------------------------
  // -------------------------------- Component Template -------------------------------------
  // -----------------------------------------------------------------------------------------

  return (
    <Provider store={store}>
      <NeoErrorBoundary>
        <NeoThemeProvider>
          <Intl.IntlProvider locale={"en"} messages={messages["en"]}>
            <NeoComponents.NeoTooltips />
            <Toastr />
            <Header
              name={appTitle}
              applications={applications}
              activeApp={activeApp}
              pageTitle={pageTitle}
            />
            {showSidebar && applications.length > 0 && (
              <SideMenu
                isExpandedSideBar={isExpandedSideBar}
                setIsExpandedSideBar={setIsExpandedSideBar}
              />
            )}
            {globalLoader && <GlobalLoader loaderBackground="" />}
            {showWhiteLoader && <GlobalLoader loaderBackground="#fff" />}
            {showEulaAcceptance && eulaObj && (
              <SuspenseWithLoader>
                <EulaAcceptanceModal
                  setEulaObj={setEulaObj}
                  setGlobalLoader={setGlobalLoader}
                  eulaObj={eulaObj}
                  popupOpenStatus={showEulaAcceptance}
                  setPopupOpenStatus={setShowEulaAcceptance}
                  hideHeader={false}
                />
              </SuspenseWithLoader>
            )}
            {foneAlertConfig.status && (
              <FoneAlert alertData={foneAlertConfig.alertProps} />
            )}
            <BrowserRouter basename="/">
              <Routes>
                <Route path={`/editprofile`} element={
                  <SuspenseWithLoader>
                    <UserProfileContainer />
                  </SuspenseWithLoader>}>
                  <Route
                    path="*"
                    element={<Navigate to={`/editprofile`} replace />}
                  />
                </Route>
                {applications?.length > 0 && (
                  <>
                    <Route
                      path="/ds/contact-support"
                      element={
                        <SuspenseWithLoader>
                          <ContactSupport />
                        </SuspenseWithLoader>}
                    />
                    <Route
                      path="/dspm/contact-support"
                      element={
                        <SuspenseWithLoader>
                          <ContactSupport />
                        </SuspenseWithLoader>}
                    />
                  </>
                )}
              </Routes>
            </BrowserRouter>
          </Intl.IntlProvider>
        </NeoThemeProvider>
      </NeoErrorBoundary>
    </Provider>
  )
}
